import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav    from '../components/Landing/LandingNav';
import LandingFooter from '../components/Landing/LandingFooter';

const EFFECTIVE_DATE = 'March 6, 2026';
const LAST_UPDATED   = 'March 6, 2026';
const COMPANY        = 'Eternium LLC';
const PRODUCT        = 'Helix';
const CONTACT_EMAIL  = 'privacy@helix.ai';

// ── Shared typography helpers ─────────────────────────────────────────────────
const H2 = ({ id, children }) => (
    <h2 id={id} className="text-xl font-heading font-bold text-white mt-10 mb-4 scroll-mt-24">
        {children}
    </h2>
);
const H3 = ({ children }) => (
    <h3 className="text-base font-bold text-white/90 mt-6 mb-2">{children}</h3>
);
const P  = ({ children }) => (
    <p className="text-[var(--text-secondary)] leading-relaxed mb-4">{children}</p>
);
const UL = ({ children }) => (
    <ul className="list-disc list-inside text-[var(--text-secondary)] leading-relaxed mb-4 space-y-1.5 pl-2">
        {children}
    </ul>
);
const LI = ({ children }) => <li>{children}</li>;

// ─────────────────────────────────────────────────────────────────────────────
export default function Privacy() {
    return (
        <div className="min-h-screen bg-[var(--bg-dark)] text-white">
            <LandingNav />

            {/* Hero */}
            <div className="pt-32 pb-10 px-6 border-b border-white/6">
                <div className="max-w-3xl mx-auto">
                    <span className="text-xs font-bold uppercase tracking-widest text-[var(--primary-color)] mb-3 block">
                        Legal
                    </span>
                    <h1 className="text-4xl font-heading font-black text-white mb-3">Privacy Policy</h1>
                    <p className="text-sm text-[var(--text-secondary)]">
                        Effective: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {LAST_UPDATED}
                    </p>
                </div>
            </div>

            {/* Body */}
            <div className="max-w-3xl mx-auto px-6 py-12">

                <P>
                    {COMPANY} ("we," "us," or "our") operates the {PRODUCT} platform. This Privacy
                    Policy explains how we collect, use, disclose, and protect information about you
                    when you use our Service. By using the Service you agree to the practices described
                    in this policy.
                </P>

                {/* ── 1. Information We Collect ────────────────────────────────────────── */}
                <H2 id="collect">1. Information We Collect</H2>

                <H3>1.1 Account Information</H3>
                <P>
                    When you register, we collect your name, email address, and a hashed password.
                    If you sign up via a third-party OAuth provider (e.g., Google), we receive your
                    name, email, and profile picture from that provider.
                </P>

                <H3>1.2 Usage Data</H3>
                <P>
                    We automatically collect information about how you interact with the Service,
                    including:
                </P>
                <UL>
                    <LI>Pages and features accessed, and timestamps of those accesses.</LI>
                    <LI>AI generation requests (model selected, prompt text, generation parameters).</LI>
                    <LI>Credit balance changes and top-up history.</LI>
                    <LI>Project and media asset metadata (file names, types, sizes).</LI>
                    <LI>Browser type, device type, operating system, and IP address.</LI>
                    <LI>Referring URLs and session duration.</LI>
                </UL>

                <H3>1.3 Payment Information</H3>
                <P>
                    We use <strong className="text-white">Stripe</strong> to process payments.
                    {COMPANY} never stores your full credit card number, CVV, or bank account details.
                    Stripe provides us with a payment token and limited card metadata (last 4 digits,
                    card brand, expiration month/year) for display in your account. Stripe's privacy
                    practices are governed by the{' '}
                    <a
                        href="https://stripe.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--primary-color)] hover:underline"
                    >
                        Stripe Privacy Policy
                    </a>.
                </P>

                <H3>1.4 Content You Upload or Generate</H3>
                <P>
                    We store media files, storyboard data, and AI-generated outputs that you upload or
                    create through the Service. This content is stored on{' '}
                    <strong className="text-white">Cloudflare R2</strong> and is associated with your
                    account. We process this content solely to provide the Service to you.
                </P>

                <H3>1.5 Communications</H3>
                <P>
                    If you contact our support team or send us feedback, we retain those communications
                    to help resolve your inquiry and improve the Service.
                </P>

                {/* ── 2. How We Use Your Information ──────────────────────────────────── */}
                <H2 id="use">2. How We Use Your Information</H2>
                <P>We use the information we collect to:</P>
                <UL>
                    <LI>Provide, operate, and improve the Service.</LI>
                    <LI>Process payments and manage subscription billing.</LI>
                    <LI>Send transactional emails (receipts, password resets, account alerts).</LI>
                    <LI>Send product updates and marketing communications, where you have opted in.</LI>
                    <LI>Enforce our Terms of Service and Fair Use Policy.</LI>
                    <LI>Monitor and analyze usage to prevent abuse and ensure platform stability.</LI>
                    <LI>Comply with legal obligations and respond to lawful requests from authorities.</LI>
                    <LI>Aggregate and anonymize data for internal analytics and product research.</LI>
                </UL>
                <P>
                    We do <strong className="text-white">not</strong> sell your personal data to third
                    parties. We do <strong className="text-white">not</strong> use your uploaded media
                    or AI-generated content to train AI models without your explicit consent.
                </P>

                {/* ── 3. Sharing Your Information ─────────────────────────────────────── */}
                <H2 id="sharing">3. How We Share Your Information</H2>

                <H3>3.1 Service Providers</H3>
                <P>
                    We share data with trusted third-party providers that help us operate the Service:
                </P>

                {/* Providers table */}
                <div className="glass-panel rounded-2xl overflow-hidden mb-6">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/8 bg-white/[0.02]">
                                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]">Provider</th>
                                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]">Purpose</th>
                                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)] hidden md:table-cell">Data Shared</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { provider: 'Stripe', purpose: 'Payment processing', data: 'Email, billing address, payment tokens' },
                                { provider: 'Eternium API', purpose: 'AI image & video generation', data: 'Prompt text, generation parameters' },
                                { provider: 'Cloudflare R2', purpose: 'Media file storage', data: 'Encrypted file content' },
                                { provider: 'DiceBear', purpose: 'Default avatar generation', data: 'Hashed email or username (seed only)' },
                            ].map((row, i) => (
                                <tr key={row.provider} className={`border-b border-white/5 ${i % 2 === 1 ? 'bg-white/[0.02]' : ''}`}>
                                    <td className="px-5 py-3.5 text-white font-medium">{row.provider}</td>
                                    <td className="px-5 py-3.5 text-[var(--text-secondary)]">{row.purpose}</td>
                                    <td className="px-5 py-3.5 text-[var(--text-secondary)] text-xs hidden md:table-cell">{row.data}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <H3>3.2 Legal Requirements</H3>
                <P>
                    We may disclose your information when required by law, subpoena, or other legal
                    process, or when we believe in good faith that disclosure is necessary to protect
                    our rights, protect your safety or the safety of others, investigate fraud, or
                    respond to a government request.
                </P>

                <H3>3.3 Business Transfers</H3>
                <P>
                    If {COMPANY} is involved in a merger, acquisition, or sale of assets, your
                    information may be transferred as part of that transaction. We will notify you via
                    email or a prominent in-app notice before your data is transferred and becomes
                    subject to a different Privacy Policy.
                </P>

                {/* ── 4. Data Retention ───────────────────────────────────────────────── */}
                <H2 id="retention">4. Data Retention</H2>
                <P>
                    We retain your account information and associated data for as long as your account
                    is active or as needed to provide the Service. If you delete your account, we will
                    delete or anonymize your personal data within 30 days, except where we are required
                    to retain it for legal, tax, or fraud-prevention purposes (typically up to 7 years
                    for financial records).
                </P>
                <P>
                    AI generation prompts and outputs may be retained in anonymized or aggregated form
                    for up to 12 months for safety monitoring and abuse prevention.
                </P>

                {/* ── 5. Data Security ────────────────────────────────────────────────── */}
                <H2 id="security">5. Data Security</H2>
                <P>
                    We implement industry-standard security measures including TLS encryption in transit,
                    AES-256 encryption at rest for stored files, access controls limited to authorized
                    personnel, and regular security reviews. No method of transmission or storage is
                    100% secure; we cannot guarantee absolute security.
                </P>

                {/* ── 6. Cookies & Tracking ───────────────────────────────────────────── */}
                <H2 id="cookies">6. Cookies &amp; Tracking</H2>
                <P>We use the following categories of cookies:</P>
                <UL>
                    <LI><strong className="text-white">Strictly Necessary</strong> — Session and authentication tokens required for the Service to function. Cannot be disabled.</LI>
                    <LI><strong className="text-white">Functional</strong> — Remember your preferences (e.g., sidebar state, theme). Can be disabled without losing core functionality.</LI>
                    <LI><strong className="text-white">Analytics</strong> — Aggregate, anonymized usage statistics to help us improve the Service. Opt-out available in Account settings.</LI>
                </UL>
                <P>
                    We do <strong className="text-white">not</strong> use third-party advertising
                    cookies or behavioral tracking pixels.
                </P>

                {/* ── 7. Your Rights ──────────────────────────────────────────────────── */}
                <H2 id="rights">7. Your Rights</H2>

                <H3>7.1 All Users</H3>
                <P>You have the right to access, correct, or delete your personal data at any time
                    via your Account settings or by contacting us at{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--primary-color)] hover:underline">{CONTACT_EMAIL}</a>.
                    You may also export your project data through the Service.
                </P>

                <H3>7.2 European Economic Area (GDPR)</H3>
                <P>
                    If you are located in the EEA, you have additional rights under the General Data
                    Protection Regulation including: the right to data portability, the right to
                    restrict processing, and the right to object to processing based on legitimate
                    interests. Our legal bases for processing are contract performance, legitimate
                    interests, and consent (where applicable). To exercise these rights, contact{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--primary-color)] hover:underline">{CONTACT_EMAIL}</a>.
                    You may also lodge a complaint with your local supervisory authority.
                </P>

                <H3>7.3 California Residents (CCPA / CPRA)</H3>
                <P>
                    California residents have the right to know what personal information we collect
                    and how it is used, the right to delete personal information, the right to opt out
                    of the sale of personal information (we do not sell personal information), and the
                    right to non-discrimination for exercising these rights. To submit a verifiable
                    consumer request, contact{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--primary-color)] hover:underline">{CONTACT_EMAIL}</a>.
                </P>

                {/* ── 8. Children's Privacy ───────────────────────────────────────────── */}
                <H2 id="children">8. Children's Privacy</H2>
                <P>
                    The Service is not directed to individuals under 18 years of age. We do not
                    knowingly collect personal information from children. If we learn that we have
                    collected personal information from a child under 18, we will delete that
                    information promptly. If you believe a child has provided us with personal
                    information, please contact us at{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--primary-color)] hover:underline">{CONTACT_EMAIL}</a>.
                </P>

                {/* ── 9. International Transfers ──────────────────────────────────────── */}
                <H2 id="transfers">9. International Data Transfers</H2>
                <P>
                    {COMPANY} is based in the United States. If you access the Service from outside
                    the US, your information may be transferred to and processed in the US. For
                    transfers from the EEA or UK, we rely on Standard Contractual Clauses approved
                    by the European Commission to ensure an adequate level of data protection.
                </P>

                {/* ── 10. Changes ─────────────────────────────────────────────────────── */}
                <H2 id="changes">10. Changes to This Policy</H2>
                <P>
                    We may update this Privacy Policy from time to time. Material changes will be
                    communicated via email or a prominent in-app notice at least 14 days before they
                    take effect. We encourage you to review this policy periodically.
                </P>

                {/* ── 11. Contact ─────────────────────────────────────────────────────── */}
                <H2 id="contact">11. Contact Us</H2>
                <P>
                    For privacy-related questions, data requests, or concerns, contact our Privacy
                    team at:
                </P>
                <div className="glass-panel rounded-xl p-4 text-sm text-[var(--text-secondary)] mb-8">
                    {COMPANY} — Privacy Team<br />
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--primary-color)] hover:underline">{CONTACT_EMAIL}</a>
                </div>

                {/* Links to other legal docs */}
                <div className="border-t border-white/6 pt-8 flex flex-wrap gap-4 text-sm">
                    <Link to="/terms" className="text-[var(--primary-color)] hover:underline">Terms of Service</Link>
                    <Link to="/#pricing" className="text-[var(--text-secondary)] hover:text-white transition-colors">Pricing</Link>
                    <Link to="/" className="text-[var(--text-secondary)] hover:text-white transition-colors">← Back to Helix</Link>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
}
